import type { StorageNode } from '@d/sdk/index'
import { CacheGroup, StorageListCache } from '@d/cache'
import { StorageNodeTypeEnum } from '@d/sdk/index'
import { PathGuide } from '@d/util'
import { useUserConfig } from '../../composables/useUserConfig'
import { debugMode } from '../../helper/debug'
import {
  SortMode,
  SortOption,
  sortAlphanumeric,
  sortByDate,
  sortByType,
} from '../../helper/sort'

export type TreeItem = StorageNode & {
  children?: TreeItem[]
  fullPath: string
  error: boolean
  loading?: boolean
}

const userConfig = useUserConfig()
const cacheGroup = new CacheGroup(onBeforeUnmount)
// export const treeItems: Ref<TreeItem[]> = ref([])
export const flatTreeItems = reactive(new Map<string, TreeItem>())

const hideFolders = computed(() => {
  return debugMode.value ? [] : ['.versioning']
})

const rootNode = StorageListCache.use('/', cacheGroup)
const fsHierarchyRefresh = ref(0)
export const fsHierarchy = computedWithControl(
  () => StorageListCache._operation.value + fsHierarchyRefresh.value,
  () => {
    // void _fsHierarchyDebounce.value;

    const root = rootNode.value

    if (!root) {
      return []
    }

    const allKeys = [...StorageListCache.cacheResolved.keys()]
    // console.log('ALL KEYS', allKeys)
    function getChildrenKeys(path: string) {
      return allKeys.filter((k) => {
        if (k[0] !== '/') {
          k = `/${k}`
        }
        const relativePath = k.slice(path.length + 1)
        return k.startsWith(path) && k !== path && !relativePath.includes('/')
      })
    }

    function process(path: string): TreeItem[] {
      const childrenKeys = getChildrenKeys(path).filter(k =>
        StorageListCache.exists(k) && !hideFolders.value.includes(k.split('/')?.pop() ?? ''),
      )
      const ls = StorageListCache.get(path)
      if (ls && !Array.isArray(ls)) { // failed to fetch from the remote (unavailable)
        return []
      }

      if (childrenKeys.length === 0) {
        return (
          ls?.map((node) => {
            const item = {
              ...node,
              fullPath: removeFirstSlash(`${path}/${node.name}`),
              children: node.type === 'file' ? undefined : [],
              error:
                StorageListCache.isFailed(`${path}/${node.name}`),
              loading:
                StorageListCache.isLoading(`${path}/${node.name}`),
            }
            flatTreeItems.set(item.fullPath, item)
            return item
          })?.filter(item => !hideFolders.value.includes(item.name)) ?? []
        )
      }

      function removeFirstSlash(s: string) {
        return s[0] === '/' ? s.slice(1) : s
      }

      return (
        ls?.map((node) => {
          const item = {
            ...node,
            fullPath: removeFirstSlash(`${path}/${node.name}`),
            error:
              StorageListCache.isFailed(`${path}/${node.name}`),
            children:
              node.type === 'file'
                ? undefined
                : process(`${path}/${node.name}`),
            loading:
              StorageListCache.isLoading(`${path}/${node.name}`),
          }
          flatTreeItems.set(item.fullPath, item)
          return item
        })?.filter(item => !hideFolders.value.includes(item.name)) ?? []
      )

      // return childrenKeys.map((k) => {
      //   const nodes = listCache.get(k) as HierarchyStorageNode[]
      //   console.log('CHildren keys', path, k, nodes)
      //   if (!nodes) {
      //     throw new Error('Node not found')
      //   }

      //   return nodes.map(node => ({
      //     ...node,
      //     fullPath: `${path}/${node.name}`,
      //     children: process(`${path}/${node.name}`),
      //   }))
      // }).flat()
    }

    const r = root.map((node) => {
      const item = {
        ...node,
        fullPath: `${node.name}`,
        children: process(`/${node.name}`),
        error:
          StorageListCache.isFailed(`/${node.name}`),
        loading:
          StorageListCache.isLoading(`/${node.name}`),
      }
      flatTreeItems.set(item.fullPath, item)
      return item
    })

    return r
  },
)

export const treeItems = fsHierarchy

const sortMode = userConfig.treeSorting
export const selected: Ref<string[]> = ref([])
export const opened: Ref<string[]> = ref([])
export const viewMode: Ref<boolean> = useLocalStorage('viewMode', true)
export const cache: Ref<TreeItem[]> = ref([])

export const fsHierarchySorted = computed(() =>
  sort(fsHierarchy.value, sortMode.value),
)

export function sortTree(): void {
  // Todo: Implement sorting
  // treeItems.value = sort(treeItems.value, sortMode.value)
}

function sort(items: TreeItem[], mode: SortOption): TreeItem[] {
  switch (mode) {
    case SortOption.TypeAsc:
      return sortByType(items, SortMode.Ascending)
    case SortOption.TypeDesc:
      return sortByType(items, SortMode.Descending)
    case SortOption.AlphaNumericAsc:
      return sortAlphanumeric(items, SortMode.Ascending)
    case SortOption.AlphaNumericDesc:
      return sortAlphanumeric(items, SortMode.Descending)
    case SortOption.DateAsc:
      return sortByDate(items, SortMode.Ascending)
    case SortOption.DateDesc:
      return sortByDate(items, SortMode.Descending)
    default:
      return sortByType(items, SortMode.Ascending)
  }
}

StorageListCache.on('afterInvalidate', (key, value, oldValue, reload) => {
  if (reload) {
    const target = flatTreeItems.get(key.slice(1))
    if (!target)
      return
    fetchPath(target)
    // updateTreeItemChildren(key, value as any)
  }
})

export async function initialLoad(
  paths: string[][],
  invalidate = false,
  keepOld = false,
): Promise<void> {
  if (invalidate) {
    cacheGroup.clear()
  }
  // treeItems.value = []

  const neededPaths = paths.flatMap(p =>
    PathGuide.from(p)
      .allAbove()
      .flatMap(p => p.toString()),
  )

  const nodes: Array<ReturnType<typeof StorageListCache.use>> = []

  for (const path of neededPaths) {
    nodes.push(StorageListCache.use(path, cacheGroup))
  }

  if (!keepOld)
    nodes.forEach(n => n.invalidate())

  // await Promise.all(nodes.map(n => n.tryValueAsync))
  fsHierarchyRefresh.value++
  await Promise.all(nodes.map(async n => await n.tryValueAsync))

  console.log('Inited')
}

function registerFlatTreeItems(item: TreeItem): void {
  flatTreeItems.set(item.fullPath, item)
  if (item.children) {
    item.children.forEach(c => registerFlatTreeItems(c))
  }
}

export async function fetchPath(dir: TreeItem): Promise<void> {
  StorageListCache.use(dir.fullPath, cacheGroup, false, true)
  // const childFolder = await node.tryValueAsync
  // if (!childFolder) {
  //   dir.error = true
  //   return
  // }
  // const children = sort(childFolder.map(i => mapItem(i, dir)), sortMode.value)
  // dir.children = children
}

export function itemFilter(dir: TreeItem) {
  const storagePath = PathGuide.from(dir.fullPath).storage()

  if (!storagePath)
    return true

  const storages = StorageListCache.get('/')
  if (!storages)
    return true

  const storage = storages.find(s => `/${s.name}` === storagePath.toString())

  if (storage && !storage.features.write) {
    // console.log('dir', dir)
    if (dir.name === 'binfolder.bin')
      return false
  }

  return true
}

export function mapItem(path: StorageNode, parent: TreeItem): TreeItem {
  const item: TreeItem = {
    ...path,
    fullPath: `${parent.fullPath}/${path.name}`,
    error: false,
  }
  if (path.type !== StorageNodeTypeEnum.File)
    item.children = [] as TreeItem[]

  registerFlatTreeItems(item)
  return item
}
